/*
 * The one thing the client knows about the query grammar of spec 10 §3: where the filters are in the
 * string, and how to take one out or put one in. Everything about what a filter *means* — which
 * account `from:@bea` is, whether `in:#planung` is a conversation this reader may read, whether any
 * repository is called `repo:util` — is the server's answer in `SearchResponse.parsed` and
 * `CodeSearchResponse.parsed`, and the filter bar renders those.
 *
 * Two searches, one grammar, three halves. The archive search answers the messages and the drive
 * (S-09 §3.3) and the scan answers the repositories' documents and code; which operators a query
 * carries is what decides which halves of the screen are asked at all (`halvesFor`).
 *
 * It is a scanner and not a regular expression, and it mirrors `internal/core/search/parse.go` unit
 * for unit: whitespace separates, a quoted phrase may hold spaces and is never an operator, a
 * leading `-` negates the word and therefore cannot open a filter, and a known name with an argument
 * the server cannot read (`has:image`, `after:soon`) is not a filter either — the server searches for
 * it as text, so the client must not draw a chip for it.
 *
 * Everything here is pure, which is the point: the string surgery is the part of a filter bar that
 * breaks, and it is tested on its own (queryEdit.test.ts).
 */

/** The operators the message search has, as they are typed (parse.go's applyOperator). */
export const CHAT_OPERATORS = ["in", "from", "has", "before", "after", "on"] as const

/**
 * The one only the drive's half has (S-09 D6): what kind of item — `type:document`, `type:file`,
 * `type:folder`. `in:drive/<key>` is the archive's `in:` with a drive's name, so it is no operator of
 * its own; `halvesOf` reads it as the drive's.
 */
export const DRIVE_OPERATORS = ["type"] as const

/** The words `type:` takes, as the server reads them (parse.go). */
export const ITEM_TYPES = ["document", "file", "folder"] as const

export type ItemType = (typeof ITEM_TYPES)[number]

/**
 * The two the code search has (S-07 §3.6): which repository, and which directory inside it. They are
 * stripped from the pattern by the server, as the chat operators are, and echoed back in its parse.
 */
export const CODE_OPERATORS = ["repo", "path"] as const

/** Every operator this build knows, in the order the halves of the screen are drawn. */
export const OPERATORS = [...CHAT_OPERATORS, ...DRIVE_OPERATORS, ...CODE_OPERATORS] as const

export type ChatOperator = (typeof CHAT_OPERATORS)[number]
export type CodeOperator = (typeof CODE_OPERATORS)[number]
export type Operator = (typeof OPERATORS)[number]

/** The three halves of the screen, in the order they are drawn. */
export const HALVES = ["messages", "drive", "code"] as const

export type Half = (typeof HALVES)[number]

/** The prefix of an `in:` argument that names a shared drive rather than a conversation. */
export const DRIVE_SCOPE_PREFIX = "drive/"

/** Which half of the screen an operator belongs to; a filter no half claims is not a filter. */
export function isCodeOperator(operator: Operator): operator is CodeOperator {
	return (CODE_OPERATORS as readonly string[]).includes(operator)
}

/**
 * The halves a filter narrows. A person and a day are things messages and documents both have, so
 * `from:` and the dates are shared; a conversation and an attachment are the messages', a kind of
 * item and a shared drive the drive's, a repository and a directory the code's.
 */
export function halvesOf(filter: Pick<QueryFilter, "operator" | "argument">): readonly Half[] {
	switch (filter.operator) {
		case "from":
		case "before":
		case "after":
		case "on":
			return ["messages", "drive"]
		case "in":
			return filter.argument.startsWith(DRIVE_SCOPE_PREFIX) ? ["drive"] : ["messages"]
		case "has":
			return ["messages"]
		case "type":
			return ["drive"]
		case "repo":
		case "path":
			return ["code"]
	}
}

/** One filter of a query, and where in the string it stands. */
export interface QueryFilter {
	operator: Operator
	/** The argument as typed, without the `#` or `@` the server also ignores. */
	argument: string
	/** The token as it stands in the query: what removing this filter takes out. */
	token: string
	/** Its position among the tokens, so two filters that read alike are still two chips. */
	at: number
}

/** One unit of the query: a word, a phrase, or a filter. */
interface Token {
	/** The text as it stands in the string, quotes and all: what removing this token takes out. */
	raw: string
	/** The filter it opens, or nothing — then it is a word the search looked for. */
	filter: { operator: Operator; argument: string } | undefined
}

/**
 * The filters of a query, in the order they were typed. The bar pairs the nth filter of an operator
 * with the nth entry the server resolved for it.
 */
export function filtersIn(query: string): QueryFilter[] {
	const out: QueryFilter[] = []
	tokenize(query).forEach((token, at) => {
		if (token.filter === undefined) return
		out.push({
			operator: token.filter.operator,
			argument: token.filter.argument,
			token: token.raw,
			at,
		})
	})
	return out
}

/** Which halves this reader may be asked at all: the code is the team's, the drive the instance's. */
export type HalvesAllowed = Record<Exclude<Half, "messages">, boolean>

/**
 * Which halves of the screen a query asks for (S-07 §3.6, S-09 §3.3).
 *
 * A half is asked when the query carries a filter of its own, or no filter that is not its own: a
 * conversation and an attachment are things only messages have, so a query carrying `in:#` or `has:`
 * leaves the drive and the code out — their sections are *absent* rather than empty — and the mirror
 * holds for `type:` and `in:drive/…` and for `repo:` and `path:`.
 *
 * A query that carries filters of several halves asks for each of them — each searches what it
 * understands and says, through the chips, what it searched for as text instead. A query no half it
 * may ask can answer still asks the messages: a screen with a term on it and no answer anywhere is
 * the one outcome that is never right.
 *
 * A reader the code search is not for — a guest; the server refuses them its route — never asks it,
 * and an instance without the drive never asks the drive (`allowed`): a section that could only ever
 * say "refused" is a door that ends in a refusal, so it is not drawn at all. `only` narrows the screen
 * to one half — *Drive only*, which the drive's own search field asks for.
 */
export function halvesFor(
	query: string,
	allowed: HalvesAllowed,
	only?: Half,
): Record<Half, boolean> {
	const filters = filtersIn(query)
	const may = (half: Half) => half === "messages" || allowed[half]
	if (only !== undefined && may(only)) {
		return { messages: only === "messages", drive: only === "drive", code: only === "code" }
	}
	const asked = (half: Half) =>
		may(half) &&
		(filters.some((filter) => halvesOf(filter).includes(half)) ||
			filters.every((filter) => halvesOf(filter).includes(half)))
	const out = { messages: asked("messages"), drive: asked("drive"), code: asked("code") }
	if (!out.messages && !out.drive && !out.code) out.messages = true
	return out
}

/**
 * The query as one half is asked it: without the operators only other halves have.
 *
 * A mixed query — `repo:teamBase from:@bea` — asks for several halves, and the message search has no
 * `repo:`. Sent as it stands, it searches for the characters `repo:teamBase` as text, finds nothing
 * and reports the word back as "searched as text" — directly beside the chip *In teamBase* that the
 * same word produced in the other half. Two contradictory statements about one word is worse than
 * either of them, and the honest request is the one the half can answer.
 */
export function forHalf(query: string, half: Half): string {
	return join(
		tokenize(query).filter(
			(token) => token.filter === undefined || halvesOf(token.filter).includes(half),
		),
	)
}

/** True when the query carries at least one filter, i.e. when it asked for less than everywhere. */
export function hasFilter(query: string): boolean {
	return tokenize(query).some((token) => token.filter !== undefined)
}

/** The same search without its filters: what "Search everywhere" runs. */
export function withoutFilters(query: string): string {
	return join(tokenize(query).filter((token) => token.filter === undefined))
}

/** The query without that one filter, and with every word and every other filter left alone. */
export function removeFilter(query: string, filter: QueryFilter): string {
	return join(tokenize(query).filter((_token, at) => at !== filter.at))
}

/** The query without any filter of that operator — what a menu replaces before it appends. */
export function removeOperator(query: string, operator: Operator): string {
	return join(tokenize(query).filter((token) => token.filter?.operator !== operator))
}

/**
 * The query with this filter and no other of its operator: a menu is a choice, so picking a second
 * person replaces the first rather than asking for messages by both. The three date operators are
 * one choice as well — `on:` and `after:` in one query would be a range nobody asked for.
 */
export function setFilter(query: string, operator: Operator, argument: string): string {
	const operators: readonly Operator[] = isDateOperator(operator) ? DATE_OPERATORS : [operator]
	let next = query
	for (const each of operators) next = removeOperator(next, each)
	return append(next, `${operator}:${argument}`)
}

/** The three that are one choice: a query with `on:` and `after:` is a range nobody asked for. */
const DATE_OPERATORS = ["before", "after", "on"] as const

function isDateOperator(operator: Operator): boolean {
	return (DATE_OPERATORS as readonly Operator[]).includes(operator)
}

function append(query: string, token: string): string {
	const head = query.trim()
	return head === "" ? token : `${head} ${token}`
}

/** The tokens joined back into a query; the whitespace between them is one space. */
function join(tokens: Token[]): string {
	return tokens.map((token) => token.raw).join(" ")
}

function tokenize(query: string): Token[] {
	const out: Token[] = []
	let index = 0
	while (index < query.length) {
		if (isSpace(query[index])) {
			index++
			continue
		}
		const start = index
		// A leading `-` negates the word, and a quoted word is a phrase: the server makes a term of
		// either, so neither can open a filter.
		let plain = true
		if (query[index] === "-" && index + 1 < query.length && !isSpace(query[index + 1])) {
			plain = false
			index++
		}
		if (query[index] === '"') {
			plain = false
			index++
			while (index < query.length && query[index] !== '"') index++
			// An unclosed quote runs to the end, which is what half a phrase somebody is still typing is.
			if (index < query.length) index++
		} else {
			while (index < query.length && !isSpace(query[index])) index++
		}
		// The trailing `*` of a prefix term belongs to the token; it never opens a filter either.
		if (query[index] === "*") index++
		const raw = query.slice(start, index)
		out.push({ raw, filter: plain ? filterOf(raw) : undefined })
	}
	return out
}

function isSpace(character: string | undefined): boolean {
	return character === " " || character === "\t" || character === "\n" || character === "\r"
}

/**
 * The filter a bare word opens, or nothing. The argument rules are the server's: `has:` knows only
 * `file`, `type:` only the three item kinds, and a date is `YYYY-MM-DD` — anything else of that shape is text the server searched for
 * and the bar shows it as one of `parsed.unknown` instead.
 */
function filterOf(raw: string): { operator: Operator; argument: string } | undefined {
	const colon = raw.indexOf(":")
	if (colon <= 0 || colon === raw.length - 1) return undefined
	const name = raw.slice(0, colon).toLowerCase()
	if (!isOperator(name)) return undefined
	const argument = raw.slice(colon + 1)
	switch (name) {
		case "has":
			return argument.toLowerCase() === "file" ? { operator: name, argument: "file" } : undefined
		case "before":
		case "after":
		case "on":
			return isDay(argument) ? { operator: name, argument } : undefined
		case "in":
			return { operator: name, argument: argument.replace(/^#/, "") }
		case "type": {
			const word = argument.toLowerCase()
			return isItemType(word) ? { operator: name, argument: word } : undefined
		}
		case "from":
			return { operator: name, argument: argument.replace(/^@/, "") }
		// A repository is named as it is configured and a path is repository-relative; neither has a
		// shape the client can check, so both are handed over as typed and the server's parse says
		// which of them it resolved. A trailing slash on a directory is the one thing taken off —
		// `path:docs/` and `path:docs` are the same prefix, and two chips for one filter are not.
		case "repo":
			return { operator: name, argument }
		case "path": {
			const prefix = argument.replace(/\/+$/, "")
			return prefix === "" ? undefined : { operator: name, argument: prefix }
		}
	}
}

function isItemType(word: string): word is ItemType {
	return (ITEM_TYPES as readonly string[]).includes(word)
}

function isOperator(name: string): name is Operator {
	return (OPERATORS as readonly string[]).includes(name)
}

/** A day as the server reads it: `YYYY-MM-DD`, and a real date rather than the 31st of February. */
export function isDay(argument: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(argument)) return false
	const [year, month, day] = argument.split("-").map(Number)
	const date = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1))
	return (
		date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day
	)
}

/** A `YYYY-MM-DD` as the midday of that day here, which is what a date is formatted from. */
export function dayAsTime(argument: string): number {
	const [year, month, day] = argument.split("-").map(Number)
	return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, 12).getTime()
}

/** The `YYYY-MM-DD` of a moment here, which is what a date preset puts into the query. */
export function timeAsDay(ms: number): string {
	const date = new Date(ms)
	const month = String(date.getMonth() + 1).padStart(2, "0")
	const day = String(date.getDate()).padStart(2, "0")
	return `${String(date.getFullYear())}-${month}-${day}`
}
